import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  TextField,
  IconButton,
  Paper,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import dayjs from 'dayjs';

export default function BloodResultsViewer({
  bloodSets,
  onChange,
  isScenarioBloods = false,
}) {
  const handleValueEdit = (id, groupIdx, elIdx, newValue) => {
    const updated = bloodSets.map((set) => {
      if (set.id !== id) return set;

      return {
        ...set,
        groups: set.groups.map((group, gIdx) => {
          if (gIdx !== groupIdx) return group;

          return {
            ...group,
            values: group.values.map((val, vIdx) => {
              if (vIdx !== elIdx) return val;
              return { ...val, value: newValue };
            }),
          };
        }),
      };
    });

    onChange(updated);
  };

  const handleDeleteSet = (id) => {
    const updated = bloodSets.filter((set) => set.id !== id);
    onChange(updated);
  };

  const sortedSets = [...(bloodSets || [])].sort(
    (a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf()
  );

  if (sortedSets.length === 0) {
    return (
      <Typography color="text.secondary">
        No blood result sets added yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {sortedSets.map((set) => {
        const groupLabels = (set.groups ?? [])
          .map((group) => group.label ?? group.groupKey ?? '')
          .filter(Boolean);

        const summaryTitle = isScenarioBloods
          ? `${set.name ? `${set.name} — ` : ''}`
          : `${set.time_offset ?? 0} ${set.time_offset_unit ?? 'hours'} ago`;

        return (
          <Accordion
            key={set.id}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid #d1d5db',
              borderRadius: '12px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
                minHeight: 64,
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                  alignItems: 'center',
                },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 0.5 }}
                  >
                    <ScienceOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {summaryTitle}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    {groupLabels.map((label) => (
                      <Chip
                        key={`${set.id}-${label}`}
                        label={label}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>

                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSet(set.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={2.5}>
                {(set.groups || []).map((group, groupIdx) => (
                  <Paper
                    key={`${set.id}-${group.label || group.groupKey || groupIdx}`}
                    elevation={0}
                    sx={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {group.label}
                      </Typography>

                      <Grid container spacing={1.5}>
                        {(group.values || []).map((el, elIdx) => (
                          <Grid item xs={12} md={6} xl={4} key={el.name}>
                            <Paper
                              elevation={0}
                              sx={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 2,
                                px: 1.5,
                                py: 1.25,
                              }}
                            >
                              <Stack spacing={1}>
                                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                  {el.name}
                                </Typography>

                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto',
                                    gap: 1,
                                    alignItems: 'center',
                                  }}
                                >
                                  <TextField
                                    value={el.value}
                                    onChange={(e) => handleValueEdit(group, elIdx, e.target.value)}
                                    size="small"
                                    fullWidth
                                  />

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ minWidth: 52 }}
                                  >
                                    {el.units}
                                  </Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary">
                                  Ref: {el.refLow} – {el.refHigh}
                                </Typography>
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}